/**
 * **Where each target's mini forecast badge sits** (`projects/target-mini-forecasts.md` 1.3). While
 * an attack is armed, every legal target carries a small badge (`+1 ACC · 62%`). The badge goes
 * through the token-label solver (`labelLayout.ts`'s `solveLabelLayout`), not a second solver, as
 * one more set of items placed AFTER the name labels, against everything already on the board:
 *
 *   - every token's footprint rect (which holds the token art, its side ring and its "mine" ring:
 *     `pixiTokenSprite.ts` keeps all of them inside the footprint box);
 *   - every drawn name label (a hidden one occupies nothing);
 *   - the caller's token-decoration obstacles, the same rects the name-label solve receives
 *     (`turn-state-on-the-map` is the project that starts feeding them);
 *   - the other badges, which the solver keeps apart as it does labels.
 *
 * The name labels are solved first and never move for a badge: a badge is transient, the names are
 * not, and a board that reshuffled every name each time an aim was armed would read worse than one
 * where the badge takes the space that is left.
 *
 * Each badge's own token rect is padded by {@link targetRingOverhang} before it is handed in: the
 * target ring (`setTargetRings`) is a hex reaching past the footprint's edges and pulses
 * larger still, and a badge parked on the ring's tip would sit on the thing that marks the target.
 * The pad is invisible to the caller: offsets come back relative to the footprint's top-left, the
 * same frame a name label's `dx`/`dy` use.
 *
 * A badge the solver cannot place anywhere clean comes back `"hidden"` and is not drawn; the ring
 * still marks the target, and hovering it expands the full forecast.
 *
 * Rects in, offsets out, like the solver: no Pixi, no combatants.
 */
import { CELL } from "./combatCells";
import { markRingRadius } from "./hexGeometry";
import { solveLabelLayout, type PlacedLabel, type Rect } from "./labelLayout";

/** How far the target ring's pulsing hex reaches past a footprint's edge, in board px: its
 *  circumradius ({@link markRingRadius}) scaled up to 1.16 at the pulse's peak (`pixiCombatBoard.ts`
 *  `setTargetRings` and its ticker), against the footprint's half side. */
export function targetRingOverhang(side: number): number {
  return markRingRadius(side, CELL) * 1.16 - side / 2;
}

export type TargetBadgeItem = {
  id: string;
  /** The target's footprint rect. */
  token: Rect;
  /** The badge pill's measured size. */
  badge: { w: number; h: number };
  /** The candidate index this badge held on the previous solve, fed back so it does not jump. */
  prevSlot?: number;
};

export type TargetBadgeLayoutInput = {
  badges: TargetBadgeItem[];
  /** Every token's drawn rect on the board (its footprint padded by the hex overflow), targets included. */
  tokens: Rect[];
  /** Every DRAWN name-label rect. */
  labels: Rect[];
  /** Token decorations outside a footprint; the same list the name-label solve gets. */
  obstacles?: Rect[];
};

/** Solve every badge's placement. Offsets are from the footprint's top-left. */
export function placeTargetBadges(input: TargetBadgeLayoutInput): Map<string, PlacedLabel> {
  const pads = new Map<string, number>();
  const items = input.badges.map((b) => {
    const pad = targetRingOverhang(b.token.w);
    pads.set(b.id, pad);
    // No `footprintCells`: a badge never goes inside a token, whatever its size.
    return {
      id: b.id,
      token: { x: b.token.x - pad, y: b.token.y - pad, w: b.token.w + pad * 2, h: b.token.h + pad * 2 },
      label: b.badge,
      ...(b.prevSlot !== undefined ? { prevSlot: b.prevSlot } : {}),
    };
  });
  const solved = solveLabelLayout(items, [...input.tokens, ...input.labels, ...(input.obstacles ?? [])]);
  const out = new Map<string, PlacedLabel>();
  for (const [id, p] of solved) {
    const pad = pads.get(id) ?? 0;
    out.set(id, { ...p, dx: p.dx - pad, dy: p.dy - pad });
  }
  return out;
}

/** The world-space rect a placed badge occupies. */
export function badgeRect(token: Rect, badge: { w: number; h: number }, placed: PlacedLabel): Rect {
  return { x: token.x + placed.dx, y: token.y + placed.dy, w: badge.w, h: badge.h };
}
